import React from 'react';
import { NoItemsFound } from '~/shared/components/NoItemsFound';
import { getAdmissionOptions, getFaceToFaceOptions, getPatientPhysiciansOptions, PatientPhysicianAssociationEnum } from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { PatientPhysicianTable } from './PatientPhysicianTable';
import { AdditionalPhysicians } from './AdditionalPhysicians';
import { PatientPhysicianFormModal } from './forms/PatientPhysicianFormModal';
import { QuickAddNew } from '~/shared/QuickAdd';
import { Button } from '~/shared/components/buttons/Button';
import { UserPen } from 'lucide-react';

type PhysicianOption = {
	id: string;
	name: string;
};

export function PatientPhysicians({ stayId }: { stayId: string }) {
	const { data: patientPhysicians } = useSuspenseQuery(getPatientPhysiciansOptions(stayId!));
	const { data: admission } = useSuspenseQuery(getAdmissionOptions(stayId));
	const { data: f2f } = useSuspenseQuery(getFaceToFaceOptions(stayId));
	const [selectedQuickAddId, setSelectedQuickAddId] = React.useState<string | null>(null);

	const physicianIds = patientPhysicians.physicians.map(row => row.physicianId);
	const quickAdds: PhysicianOption[] = [];
	if (!physicianIds.includes(admission.referral.referringPhysicianId)) {
		quickAdds.push({
			name: 'Add Referring Physician - ' + admission.referral.referringPhysician.fullName,
			id: admission.referral.referringPhysicianId
		});
	}
	if (f2f && f2f.faceToFacePhysicianId && !physicianIds.includes(f2f.faceToFacePhysicianId)) {
		quickAdds.push({
			name: 'Add Face-to-face Physician - ' + f2f.faceToFacePhysician?.fullName,
			id: f2f.faceToFacePhysicianId
		});
	}

	const physicianList = patientPhysicians.physicians
		.filter(x => x.patientPhysicianAssociationId !== PatientPhysicianAssociationEnum.Referring)
		.sort((a, b) => a.patientPhysicianAssociationId - b.patientPhysicianAssociationId);

	return (
		<>
			<div className="space-y-8">
				{quickAdds.length > 0 && (
					<div className="col-span-3 mb-4 flex flex-wrap items-center">
						<QuickAddNew options={quickAdds} onSelect={val => setSelectedQuickAddId(val.id)} />
					</div>
				)}
				<NoItemsFound
					title="No physicians recorded"
					subtitle="Click the button below to add a physician"
					itemCount={patientPhysicians.physicians.length}
					renderModal={closeModal => <PatientPhysicianFormModal stayId={stayId!} closeModal={closeModal} />}
					renderTable={() => <PatientPhysicianTable rows={physicianList.filter(x => x.active)} />}
					additionalActions={
						<Button
							intent="neutral"
							appearance="outline"
							onClick={() => console.log('Change Primary')}
							isDisabled={physicianList.length === 0}
							Icon={UserPen}
							label="Change Primary"
						/>
					}
				/>
				<AdditionalPhysicians stayId={stayId} />
			</div>
			{selectedQuickAddId && (
				<PatientPhysicianFormModal stayId={stayId!} quickAddPhysicianId={selectedQuickAddId} closeModal={() => setSelectedQuickAddId(null)} />
			)}
		</>
	);
}
